/*
 * Copyright (c) 2026 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Space, Typography } from 'antd';
import { GraphContext } from '../../FlowGraph';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';
import NodeWrapper from '../../NodeWrapper';

// Misc imports
import { GRAPH_LAYOUTS, IS_AUTHENTICATED_HANDLE_IDS, NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';

const { Text } = Typography;

const IsAuthenticatedNode = ({ id, type, data, dragging, selected }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { t } = useTranslation();
  const { graphDirection } = useContext(GraphContext);

  const isVertical = graphDirection === GRAPH_LAYOUTS.VERTICAL;

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <LimitedConnectionsHandle type="target" position={isVertical ? 'top' : 'left'} />

        <div className="content">
          <Space size={6} className="node-header">
            {nodeProps.icon}
            {nodeProps.label}
          </Space>

          <Text type="secondary">
            {t('Auto-refreshes expired tokens')}
          </Text>
        </div>

        <LimitedConnectionsHandle
          type="source"
          id={IS_AUTHENTICATED_HANDLE_IDS.SUCCESS}
          source={id}
          className="border-color-success"
          style={isVertical ? { left: '33%' } : { top: '-42px' }}
          position={isVertical ? 'bottom' : 'right'}
          allowedOutgoingEdgesCount={1}
        />

        <LimitedConnectionsHandle
          type="source"
          id={IS_AUTHENTICATED_HANDLE_IDS.ERROR}
          source={id}
          className="border-color-error"
          style={isVertical ? { left: '66%' } : { top: '-22px' }}
          position={isVertical ? 'bottom' : 'right'}
          allowedOutgoingEdgesCount={1}
        />
      </Fragment>
    );
  }, [nodeProps, isVertical, id, t]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
      isEditEnabled={false}
    />
  );
};

IsAuthenticatedNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(IsAuthenticatedNode);
